import { Component, Host, Prop, h, Element } from '@stencil/core';
import { addClass } from '../../utils/helpers/helpers';

@Component({
  tag: 'lgr-button',
  styleUrl: 'button.scss',
  shadow: true,
})
export class Button {
  @Element() el!: HTMLLgrButtonElement;
  /**
   * disabled state
   */
  @Prop({ reflect: true }) disabled = false;
  /**
   * icon: material-icons
   */
  @Prop({ reflect: true }) icon?: string;
  /**
   * size: large or small
   */
  @Prop({ reflect: true }) size?: 'large' | 'small' = 'large';
  /**
   * full width of lgr-button
   */
  @Prop({ reflect: true }) fullWidth: boolean = false;
  /**
   * variant: primary, secondary or text
   */
  @Prop({ reflect: true }) variant?: 'primary' | 'white' | 'secondary' | 'text' = 'primary';
  /**
   * variant: inverse or not. This property has no effect if the variant is set to 'primary'.
   */
  @Prop({ reflect: true }) inverse?: boolean = false;
  /**
   * loading state
   */
  @Prop({ reflect: true }) loading: boolean = false;
  /**
   * The type of the button.
   */
  @Prop() type: 'submit' | 'reset' | 'button' = 'button';
  /**
   * The HTML form element or form element id. Used to submit a form when the button is not a child of the form.
   */
  @Prop() form?: string | HTMLFormElement;
  /**
   * Anchor tag instead of button tag
   */
  @Prop() anchor?: boolean = false;
  /**
   * the URL that the hyperlink points to.
   */
  @Prop() reference?: string = '';
  /**
   * where to display the linked URL
   */
  @Prop() target?: 'self' | 'blank' | 'parent' | 'top' = 'self';
  /**
   * download files
   */
  @Prop() download: boolean = false;

  private tag: HTMLAnchorElement | HTMLButtonElement | undefined;

  private findForm(): HTMLFormElement | null {
    const { form } = this;
    if (form instanceof HTMLFormElement) {
      return form;
    }
    if (typeof form === 'string') {
      const el = document.getElementById(form);
      if (el instanceof HTMLFormElement) {
        return el;
      }
    }
    return null;
  }

  private handleClick = (ev: Event) => {
    const { el } = this;
    if (this.type === 'submit') {
      let formEl = this.findForm();
      const { form } = this;

      if (!formEl && form !== undefined) {
        return;
      }

      if (!formEl) {
        /**
         * If the form element is not set, the button may be inside
         * of a form element. Query the closest form element to the button.
         */
        formEl = el.closest('form');
      }

      if (formEl) {
        ev.preventDefault();

        const fakeButton = document.createElement('button');
        fakeButton.type = this.type;
        fakeButton.style.display = 'none';
        formEl.appendChild(fakeButton);
        fakeButton.click();
        fakeButton.remove();
      }
    }
  };

  componentDidRender() {
    if (this.anchor && this.download) {
      this.tag?.setAttribute('download', '');
    }
  }

  render() {
    const Tag = this.anchor ? 'a' : 'button';
    return (
      <Host onClick={this.handleClick} type={this.type} style={{ pointerEvents: this.disabled || this.loading ? 'none' : undefined }} aria-disabled={this.disabled ? 'true' : null}>
        <Tag
          ref={el => this.tag = el}
          href={Tag === 'a' ? this.reference : undefined}
          target={Tag === 'a' ? `_${this.target}` : undefined}
          disabled={this.disabled}
          class={`${addClass([this.size, this.variant, this.inverse ? 'inverse' : null, this.loading ? 'loading' : null, this.fullWidth ? 'full-width' : null])}`}
        >
          {this.loading && <span class="spinner"></span>}
          {this.icon && <lgr-icon class="icon">{this.icon}</lgr-icon>}
          <span class="label">
            <slot></slot>
          </span>
        </Tag>
      </Host>
    );
  }
}
